import LoyaltyTierLabel from 'components/Common/Labels/LoyaltyTierLabel'
import LayoutContainer from 'components/Common/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextLink from 'components/Luxkit/TextLink'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import LoyaltyProgramNameLogo from 'loyalty/components/LoyaltyProgramNameLogo'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import { rem } from 'polished'
import React from 'react'
import styled from 'styled-components'
import LoyaltyAccountStatusSummary from 'loyalty/components/LoyaltyAccountStatusSummary'
import Divider from 'components/Luxkit/Divider'
import FormatDecimal from 'components/utils/Formatters/FormatDecimal'
import useCurrentLoyaltyPendingPoints from 'loyalty/hooks/useCurrentLoyaltyPendingPoints'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import ProductPaletteProvider from 'contexts/ProductPaletteContext'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import Tooltip from 'components/Luxkit/Tooltip'
import LineInfoCircleIcon from 'components/Luxkit/Icons/line/LineInfoCircleIcon'

const HeroSection = styled.div`
  padding: ${rem(28)} 0;

  &.tier-bronze {
    background: ${props => props.theme.palette.product.loyalty.bronze.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
  }

  &.tier-silver {
    background: ${props => props.theme.palette.product.loyalty.silver.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
  }

  &.tier-gold {
    background: ${props => props.theme.palette.product.loyalty.gold.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
  }

  &.tier-platinum {
    background: ${props => props.theme.palette.product.loyalty.platinum.gradientPrimaryBackground};
    color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
  }
`

const TierDivider = styled(Divider)`
  && {
    margin-top: auto;
    margin-bottom: auto;
    height: ${rem(48)};
    opacity: 0.5;

    ${mediaQueryUp.tablet} {
      height: ${rem(80)};
    }
  }

  &.tier-bronze {
    background-color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast};
  }

  &.tier-silver {
    background-color: ${props => props.theme.palette.product.loyalty.silver.normalContrast};
  }

  &.tier-gold {
    background-color: ${props => props.theme.palette.product.loyalty.gold.normalContrast};
  }

  &.tier-platinum {
    background-color: ${props => props.theme.palette.product.loyalty.platinum.normalContrast};
  }
`

const HeroContent = styled.div`
  display: grid;
  justify-content: center;
  gap: ${rem(16)};
  text-align: center;

  ${mediaQueryUp.tablet} {
    text-align: unset;
    justify-content: stretch;
    gap: 0;
    grid-template-columns: 1fr 1fr;
  }
`

function LoyaltyAccountLandingPageHero() {
  const [tier, tierName] = useCurrentLoyaltyTier()
  const firstName = useAppSelector(state => state.auth.account.givenName)
  const account = useAppSelector(state => state.loyalty.account.data!)
  const [pendingPoints] = useCurrentLoyaltyPendingPoints()

  return <ProductPaletteProvider palette={`tier-${tier}`}>
    <HeroSection className={`tier-${tier}`}>
      <LayoutContainer variant="medium">
        <HeroContent>
          <Group direction="vertical" gap={8}>
            <Group
              direction="horizontal"
              gap={12}
              horizontalAlign="center"
              verticalAlign="center"
              tabletHorizontalAlign="start"
            >
              <LoyaltyProgramNameLogo width={104} />
              <LoyaltyTierLabel kind="primary" tier={tier} format="uppercase">{tierName}</LoyaltyTierLabel>
            </Group>
            <Heading variant="heading2">
              Welcome back{firstName ? `, ${firstName}` : ''}
            </Heading>
            <BodyText variant="medium">
              Membership {account.accountNumber}
            </BodyText>
            <CSSBreakpoint min="tablet">
              <TextLink size="medium" block href="#benefits">View progress</TextLink>
            </CSSBreakpoint>
          </Group>
          <Group direction="horizontal" verticalAlign="center" gap={40}>
            <VerticalSpacer gap={4}>
              <BodyText variant="medium" colour={tier === 'platinum' ? 'tier-platinum-contrast-alt' : 'neutral-two'} align="center">
                Points
              </BodyText>
              <Heading variant="heading3" align="center"><FormatDecimal value={account.points} /></Heading>
              <BodyTextBlock
                variant="medium"
                colour={tier === 'platinum' ? 'tier-platinum-contrast-alt' : 'neutral-two'}
                align="center"
                endIcon={<Tooltip description={<>Pending points may take up to 24hrs to appear</>} placement="bottom"><LineInfoCircleIcon /></Tooltip>}
              >
                <FormatDecimal value={pendingPoints.points} /> pending
              </BodyTextBlock>
            </VerticalSpacer>
            <TierDivider className={`tier-${tier}`} kind="primary" direction="vertical" />
            <LoyaltyAccountStatusSummary />
          </Group>
          <CSSBreakpoint max="mobile">
            <TextLink size="medium" href="#benefits">View progress</TextLink>
          </CSSBreakpoint>
        </HeroContent>
      </LayoutContainer>
    </HeroSection>
  </ProductPaletteProvider>
}

export default LoyaltyAccountLandingPageHero
